/**
 * Turns user-provided SVG markup (or a bare path 'd' string) into paths usable as a "Draw on Path" textPath.
 */
const { parsePathToCommands } = require('./TextPath');

const SHAPE_SELECTOR = 'path, line, polyline, polygon, circle, ellipse, rect';
// Geometry inside these is a definition, not something the SVG actually draws
const NON_RENDERED_SELECTOR = 'defs, clipPath, mask, symbol, pattern, marker';
const MAX_CANDIDATES = 12;
const VIEW_BOX_PADDING = 0.1;

const getNumber = (element, attribute) => parseFloat(element.getAttribute(attribute)) || 0;

function shapeToPathData(element) {
    switch (element.tagName.toLowerCase()) {
        case 'path':
            return element.getAttribute('d');
        case 'line':
            return `M ${getNumber(element, 'x1')} ${getNumber(element, 'y1')} L ${getNumber(element, 'x2')} ${getNumber(
                element,
                'y2'
            )}`;
        case 'polyline':
        case 'polygon': {
            const numbers = (element.getAttribute('points') || '').match(/-?[\d.]+(?:e-?\d+)?/gi) || [];
            const points = [];
            for (let i = 0; i + 1 < numbers.length; i += 2) {
                points.push(`${numbers[i]} ${numbers[i + 1]}`);
            }
            if (points.length < 2) {
                return null;
            }
            return `M ${points.join(' L ')}${element.tagName.toLowerCase() === 'polygon' ? ' Z' : ''}`;
        }
        case 'circle':
        case 'ellipse': {
            const cx = getNumber(element, 'cx');
            const cy = getNumber(element, 'cy');
            const rx = getNumber(element, element.hasAttribute('r') ? 'r' : 'rx');
            const ry = getNumber(element, element.hasAttribute('r') ? 'r' : 'ry');
            // Starts on the left and goes over the top, so the text reads upright on the upper half
            return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx - rx} ${cy} Z`;
        }
        case 'rect': {
            const x = getNumber(element, 'x');
            const y = getNumber(element, 'y');
            return `M ${x} ${y} h ${getNumber(element, 'width')} v ${getNumber(element, 'height')} H ${x} Z`;
        }
        default:
            return null;
    }
}

// The 'd' alone ignores the transform of the element and of every group above it
function getElementMatrix(element) {
    let matrix = [1, 0, 0, 1, 0, 0];
    for (let node = element; node && node.getAttribute; node = node.parentNode) {
        const transform = node.getAttribute('transform');
        if (transform) {
            matrix = fabric.util.multiplyTransformMatrices(fabric.parseTransformAttribute(transform), matrix);
        }
    }
    return matrix;
}

function transformCommands(commands, matrix) {
    return commands.map((command) => {
        const out = [command[0]];
        for (let j = 1; j < command.length; j += 2) {
            const point = fabric.util.transformPoint({ x: command[j], y: command[j + 1] }, matrix);
            out.push(point.x, point.y);
        }
        return out;
    });
}

// Text jumps across the gap between subpaths, so only the first one is usable
function getFirstSubpath(commands) {
    const nextMove = commands.findIndex((command, index) => index > 0 && command[0] === 'M');
    return nextMove === -1 ? commands : commands.slice(0, nextMove);
}

/**
 * @param {Array} commands absolute, simplified path commands (M/L/C/Q/Z only), single subpath
 * @returns {Array} the same shape, travelled from its end to its start
 */
function reversePathCommands(commands) {
    if (!commands.length) {
        return commands;
    }
    const start = { x: commands[0][1], y: commands[0][2] };
    const segments = [];
    let current = start;
    let closed = false;
    for (let i = 1; i < commands.length; i++) {
        const command = commands[i];
        if (command[0] === 'Z') {
            closed = true;
            if (current.x !== start.x || current.y !== start.y) {
                segments.push({ from: current, command: ['L', start.x, start.y] });
            }
            current = start;
        } else {
            segments.push({ from: current, command });
            current = { x: command[command.length - 2], y: command[command.length - 1] };
        }
    }

    const reversed = [['M', current.x, current.y]];
    for (let i = segments.length - 1; i >= 0; i--) {
        const { from, command } = segments[i];
        if (command[0] === 'C') {
            reversed.push(['C', command[3], command[4], command[1], command[2], from.x, from.y]);
        } else if (command[0] === 'Q') {
            reversed.push(['Q', command[1], command[2], from.x, from.y]);
        } else {
            reversed.push(['L', from.x, from.y]);
        }
    }
    if (closed) {
        reversed.push(['Z']);
    }
    return reversed;
}

function toCandidate(pathData, matrix) {
    let commands;
    try {
        commands = getFirstSubpath(parsePathToCommands(pathData));
    } catch (error) {
        return null;
    }
    if (commands.length < 2 || commands[0][0] !== 'M') {
        return null;
    }
    if (matrix) {
        commands = transformCommands(commands, matrix);
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let pointCount = 0;
    for (const command of commands) {
        for (let j = 1; j < command.length; j += 2) {
            if (!Number.isFinite(command[j]) || !Number.isFinite(command[j + 1])) {
                return null;
            }
            minX = Math.min(minX, command[j]);
            minY = Math.min(minY, command[j + 1]);
            maxX = Math.max(maxX, command[j]);
            maxY = Math.max(maxY, command[j + 1]);
            pointCount++;
        }
    }

    const segmentsInfo = fabric.util.getPathSegmentsInfo(commands);
    const length = segmentsInfo[segmentsInfo.length - 1].length;
    if (!(length > 0)) {
        return null;
    }

    const size = Math.max(maxX - minX, maxY - minY);
    const padding = size * VIEW_BOX_PADDING;
    return {
        pathData: fabric.util.joinPath(commands),
        length,
        pointCount,
        // Preview helpers: where to frame the path, how big it is and where the text starts
        viewBox: [minX - padding, minY - padding, maxX - minX + padding * 2, maxY - minY + padding * 2].join(' '),
        size,
        start: { x: commands[0][1], y: commands[0][2] }
    };
}

/**
 * @param {string} source SVG markup or a bare path 'd' string
 * @returns {Array<{pathData: string, length: number, pointCount: number, viewBox: string, size: number,
 *   start: {x: number, y: number}}>} usable paths, longest first
 */
function parseTextPathSource(source) {
    const text = (source || '').trim();
    if (!text) {
        return [];
    }
    if (!text.includes('<')) {
        const candidate = toCandidate(text);
        return candidate ? [candidate] : [];
    }

    // text/html: lenient with pasted fragments (no xmlns, no root) and DOMParser never runs scripts
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const candidates = [];
    doc.querySelectorAll(SHAPE_SELECTOR).forEach((element) => {
        if (element.closest(NON_RENDERED_SELECTOR)) {
            return;
        }
        const pathData = shapeToPathData(element);
        const candidate = pathData && toCandidate(pathData, getElementMatrix(element));
        if (candidate) {
            candidates.push(candidate);
        }
    });
    return candidates.sort((a, b) => b.length - a.length).slice(0, MAX_CANDIDATES);
}

module.exports = { parseTextPathSource, reversePathCommands };
